import React, { useState, useEffect, useContext } from 'react';
import './Messages.css';
import baseApi from '../../common/api/base';
import convenienceApi from '../../common/api/convenience';
import viewport from '../../common/viewport';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import MonitoredIndividualThreadList from '../MonitoredIndividualThreadList/MonitoredIndividualThreadList';
import ThreadList from '../ThreadList/ThreadList';
import Thread from '../Thread/Thread';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NewConversationModal from '../NewConversationModal/NewConversationModal';
import DefaultMonitoredIndividual from '../DefaultMonitoredIndividualIcon/DefaultMonitoredIndividualIcon';
import { isBefore, parseISO } from 'date-fns';
import logComponent from '../../common/logComponent';

export default function Messages() {
  const environmentContext = useContext(EnvironmentContext);
  const [monitoredIndividualThreadCollections, setMonitoredIndividualThreadCollections] = useState(null);
  const [threads, setThreads] = useState(null);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState(false);
  const [visibleMonitoredIndividual, setVisibleMonitoredIndividual] = useState(null);
  const [visibleThread, setVisibleThread] = useState(null);
  const [messageThreadReloadInterval, setMessageThreadReloadInterval] = useState(null);
  const [newConversationModalIsVisible, setNewConversationModalIsVisible] = useState(false);
  const [currentMobileView, setCurrentMobileView] = useState(environmentContext.activeUser.groupTypeString === 'Facility' ? 'monitoredIndividuals' : 'threads');
  const [singlePanelView, setSinglePanelView] = useState(viewport.getCurrent() === 'sm' || (viewport.getCurrent() === 'md' && environmentContext.activeUser.groupTypeString === 'Facility'));
  const [lastReload, setLastReload] = useState(null);
  const [needsSortMessageThreads, setNeedsSortMessageThreads] = useState(false);
  const { activeUser: user, hasToken } = environmentContext;

  if (logComponent('Messages')) { console.debug('Messages rendered'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('Messages')) { console.debug('Messages onMount'); }
    window.addEventListener('resize', checkViewport);

    setMessageThreadReloadInterval(setInterval(silentReloadMessageThreads, 30000));

    if (!hasToken) return;

    return () => clearInterval(messageThreadReloadInterval);
  }, []);

  useEffect(() => {
    if (logComponent('Messages')) { console.debug('Messages useEffect  [environmentContext.hasToken]'); }
    if (!hasToken) return;
    loadMessageThreads();
  }, [environmentContext.hasToken])

  useEffect(() => {
    if (logComponent('Messages')) { console.debug('Messages useEffect  [monitoredIndividualThreadCollections]'); }
    if (!hasToken) return;
    if (visibleMonitoredIndividual) {
      let newMonitoredIndividualThreadCollection = monitoredIndividualThreadCollections.find(collection =>
        collection.monitoredIndividual.monitoredIndividualId === visibleMonitoredIndividual.monitoredIndividualId);

      const newThreads = newMonitoredIndividualThreadCollection ? newMonitoredIndividualThreadCollection.threads : null;
      setThreads(newThreads);
    } else {
      setThreads(null);
    }
  }, [monitoredIndividualThreadCollections])

  useEffect(() => {
    if (logComponent('Messages')) { console.debug('Messages useEffect  [environmentContext.selectedMonitoredIndividual]'); }
    if (!hasToken) return;
    if (environmentContext.activeUser.groupTypeString === 'Family') {
      setVisibleThread(null);
    }
  }, [environmentContext.selectedMonitoredIndividual])

  useEffect(() => {
    if (logComponent('Messages')) { console.debug('Messages useEffect  [needsSortMessageThreads]'); }
    if (needsSortMessageThreads === true)  {
      sortMessageThreads();
      setNeedsSortMessageThreads(false);
    }
  }, [needsSortMessageThreads])

  const checkViewport = () => {
    if (logComponent('Messages')) { console.debug('Messages checkViewport'); }
    const newSinglePanelView = 
      viewport.getCurrent() === 'sm' || 
      (viewport.getCurrent() === 'md' && environmentContext.activeUser.groupTypeString === 'Facility');

    if (singlePanelView !== newSinglePanelView) {
      setSinglePanelView(newSinglePanelView);
    }  
  }

  const silentReloadMessageThreads = () => {
    if (logComponent('Messages')) { console.debug('Messages silentReloadMessageThreads'); }

    if (!hasToken) return;

    convenienceApi.getMessageThreads(user)
      .then(
        result => {
          if (user.groupTypeString === 'Family') {
            setThreads(result);
            setThreadsLoading(false);
            setThreadsError(false);
            setLastReload(Date.now());
          } else if (user.groupTypeString === 'Facility') {
            setMonitoredIndividualThreadCollections(result);
            setThreadsLoading(false);
            setThreadsError(false);
            setLastReload(Date.now());
          }
        },
        error => { });
  }

  const hideNewConversationModal = () => {
    if (logComponent('Messages')) { console.debug('Messages hideNewConversationModal'); }
    setNewConversationModalIsVisible(false);
  }

  const showNewConversationModal = () => {
    if (logComponent('Messages')) { console.debug('Messages showNewConversationModal'); }
    setNewConversationModalIsVisible(true);  
  }

  const loadMessageThreads = () => {
    if (logComponent('Messages')) { console.debug('Messages loadMessageThreads'); }

    if (!hasToken) return;

    setMonitoredIndividualThreadCollections(null);
    setThreads(null);
    setThreadsLoading(true);
    setThreadsError(false);

    convenienceApi.getMessageThreads(user)
      .then(
        result => {
          if (user.groupTypeString === 'Family') {
            setThreads(result);
            setThreadsLoading(false);
            setThreadsError(false);
          } else if (user.groupTypeString === 'Facility') {
            setMonitoredIndividualThreadCollections(result);
            setVisibleMonitoredIndividual(result.length > 0 ? result[0].monitoredIndividual : null);
            setThreads(result.length > 0 ? result[0].threads : null);
            setThreadsLoading(false);
            setThreadsError(false);
          }
        },
        error => {
          setMonitoredIndividualThreadCollections(null);
          setThreads(null);
          setThreadsLoading(false);
          setThreadsError(true);
        });
  }

  const setThreadsThis = (newThreads) => {
    if (logComponent('Messages')) { console.debug('Messages setThreadsThis'); }
    setVisibleMonitoredIndividual(newThreads.monitoredIndividual);
    setThreads(newThreads.threads);
    setVisibleThread(null);
    setCurrentMobileView('threads');
  }

  const setVisibleThreadThis = (newVisibleThread) => {
    if (logComponent('Messages')) { console.debug('Messages setVisibleThreadThis'); }

    if (!hasToken) return;

    // Facility users
    if (user.groupTypeString === 'Facility') {
      // Update read status if necessary
      if (!newVisibleThread.isReadByFacility) {
        const newMonitoredIndividualThreadCollections = [...monitoredIndividualThreadCollections];
        const targetRTC = monitoredIndividualThreadCollections.find(collection => newVisibleThread.monitoredIndividualId === collection.monitoredIndividual.monitoredIndividualId);
        const targetThread = targetRTC.threads.find(thread => thread.userMessageThreadId === newVisibleThread.userMessageThreadId);
    
        targetThread.isReadByFacility = true;  

        setMonitoredIndividualThreadCollections(newMonitoredIndividualThreadCollections);
        setVisibleThread(newVisibleThread);
        setCurrentMobileView('thread');
        setNeedsSortMessageThreads(true);
  
        baseApi.UserMessage.ToggleReadStatus(newVisibleThread.userMessageThreadId, newVisibleThread.isReadByFamily, true);
      } else {
        setVisibleThread(newVisibleThread);
        setCurrentMobileView('thread');
        // NOTE: We don't sort message threads here because nothing has happened to require a re-sort
      }
    } else if (user.groupTypeString === 'Family') {
      if (!newVisibleThread.isReadByFamily) {
        const newThreads = [...threads];
        const targetThread = newThreads.find(thread => thread.userMessageThreadId === newVisibleThread.userMessageThreadId);

        targetThread.isReadByFamily = true;

        setThreads(newThreads);
        setVisibleThread(newVisibleThread);
        setCurrentMobileView('thread');
        setNeedsSortMessageThreads(true);
  
        baseApi.UserMessage.ToggleReadStatus(newVisibleThread.userMessageThreadId, newVisibleThread.isReadByFamily, true);        

      }
      setVisibleThread(newVisibleThread);
      setCurrentMobileView('thread');
    }
  }

  const getMobileHeading = () => {
    if (logComponent('Messages')) { console.debug('Messages getMobileHeading'); }

    switch (currentMobileView) {
      case 'monitoredIndividuals':
        return <p className="Messages__mobile-heading-text">Messages</p>;
      case 'threads': 
        return (
          user.groupTypeString === 'Family' ?
            <p className="Messages__mobile-heading-text">Conversations</p> :
            <React.Fragment>
            <button 
              className="Messages__back-button"
              onClick={goBack} >
              <FontAwesomeIcon icon="chevron-left" className="Messages__back-button-icon" />
              Back
            </button>
            <p className="Messages__mobile-heading-text">Conversations</p>
            </React.Fragment>
          );
      case 'thread': 
        return (
          <React.Fragment>
            <button 
              className="Messages__back-button"
              onClick={goBack} >
              <FontAwesomeIcon icon="chevron-left" className="Messages__back-button-icon" />
              Back
            </button>
            <p className="Messages__mobile-heading-text">{ visibleThread ? (visibleThread.isPrivate ? 'Direct message' : 'Family message') : null }</p>
            </React.Fragment>
          );
      default:
        return null;
    }
  }

  const goBack = () => {
    if (logComponent('Messages')) { console.debug('Messages goBack'); }
    let newMobileView = currentMobileView === 'thread' ? 'threads' : 'monitoredIndividuals';
    setCurrentMobileView(newMobileView);
  }

  const updateVisibleThreadLastActive = () => {
    if (logComponent('Messages')) { console.debug('Messages updateVisibleThreadLastActive'); }
      
    if (user.groupTypeString === 'Facility') {
      updateVisibleThreadLastActiveForFacility();
    } else if (user.groupTypeString === 'Family') {
      updateVisibleThreadLastActiveForFamily();
    }
  }

  const updateVisibleThreadLastActiveForFamily = () => {
    if (logComponent('Messages')) { console.debug('Messages updateVisibleThreadLastActiveForFamily'); }

    const newThreads = [...threads];
    let targetThread = threads.find(thread =>  thread.userMessageThreadId === visibleThread.userMessageThreadId);
    
    let date = new Date();
    date = date.toJSON();
    targetThread.lastActive = date.substring(0,date.length - 1);

    setThreads(newThreads);
    setNeedsSortMessageThreads(true);
  }

  const updateVisibleThreadLastActiveForFacility = () => {
    if (logComponent('Messages')) { console.debug('Messages updateVisibleThreadLastActiveForFacility'); }

    const newMonitoredIndividualThreadCollections = [...monitoredIndividualThreadCollections];

    const monitoredIndividualThreadCollection = newMonitoredIndividualThreadCollections
    .find(collection => collection.monitoredIndividual.monitoredIndividualId === visibleThread.monitoredIndividualId)

    let monitoredIndividualThread = monitoredIndividualThreadCollection.threads.find(thread => thread.userMessageThreadId === visibleThread.userMessageThreadId);    
    
    let date = new Date();
    date = date.toJSON();
    monitoredIndividualThread.lastActive = date.substring(0,date.length - 1);

    setMonitoredIndividualThreadCollections(newMonitoredIndividualThreadCollections);
    setNeedsSortMessageThreads(true);
  }

  const unreadVisibleThread = (newThread) => {
    if (logComponent('Messages')) { console.debug('Messages unreadVisibleThread'); }
   const { activeUser: user } = environmentContext;

    if (user.groupTypeString === 'Facility') {
      unreadVisibleThreadForFacilityUser(newThread);
    } else if (user.groupTypeString === 'Family') {
      unreadVisibleThreadForFamilyUser(newThread);
    }
  }

  const unreadVisibleThreadForFamilyUser = (newThread) => {
    if (logComponent('Messages')) { console.debug('Messages unreadVisibleThreadForFamilyUser'); }

    const newThreads = [...threads];

    let targetThread = threads.find(thread =>  thread.userMessageThreadId === newThread.userMessageThreadId);
    targetThread = newThread;

    setThreads(newThreads);
    setNeedsSortMessageThreads(true);
  }

  const unreadVisibleThreadForFacilityUser = (newThread) => {
    if (logComponent('Messages')) { console.debug('Messages unreadVisibleThreadForFacilityUser'); }

    const newMonitoredIndividualThreadCollections = [...monitoredIndividualThreadCollections];

    const monitoredIndividualThreadCollection = newMonitoredIndividualThreadCollections
    .find(collection => collection.monitoredIndividual.monitoredIndividualId === newThread.monitoredIndividualId)

    let monitoredIndividualThread = monitoredIndividualThreadCollection.threads.find(thread => thread.userMessageThreadId === newThread.userMessageThreadId);
    monitoredIndividualThread = newThread;

    setMonitoredIndividualThreadCollections(newMonitoredIndividualThreadCollections);
    setNeedsSortMessageThreads(true);
  }

  const sortMessageThreadsForFamilyUser = () => {
    if (logComponent('Messages')) { console.debug('Messages sortMessageThreadsForFamilyUser'); }

    if (!threads) return;

    const sortedThreads = [...threads];

    sortedThreads.sort((a, b) => {
      if (!a.isReadByFamily && b.isReadByFamily) return -1;
      if (!b.isReadByFamily && a.isReadByFamily) return 1;
      
      return isBefore(parseISO(a.lastActive), parseISO( b.lastActive)) ? 1 : -1
    });   

    setThreads(sortedThreads);
  }

  const sortMessageThreadsForFacilityUser = () => {
    if (logComponent('Messages')) { console.debug('Messages sortMessageThreadsForFacilityUser'); }

    const newRTC = monitoredIndividualThreadCollections
        .find(collection => collection.monitoredIndividual.monitoredIndividualId === visibleMonitoredIndividual.monitoredIndividualId);

    if (!newRTC) return;

    const newThreads = newRTC.threads;
    const newMonitoredIndividualThreadCollections = [...monitoredIndividualThreadCollections];

    newThreads.sort((a, b) => {
      if (!a.isReadByFacility && b.isReadByFacility) return -1;
      if (!b.isReadByFacility && a.isReadByFacility) return 1;
      
      return isBefore(parseISO(a.lastActive), parseISO(b.lastActive)) ? 1 : -1
    });   

    newMonitoredIndividualThreadCollections.sort((a, b) => {
      if (!a.threads[0].isReadByFacility && b.threads[0].isReadByFacility) return -1;
      if (!b.threads[0].isReadByFacility && a.threads[0].isReadByFacility) return 1;

      return isBefore(parseISO(a.threads[0].lastActive), parseISO(b.threads[0].lastActive)) ? 1 : -1;
    });    

    setMonitoredIndividualThreadCollections(newMonitoredIndividualThreadCollections);
    setThreads(newThreads);
  }

  const sortMessageThreads = () => {
    if (logComponent('Messages')) { console.debug('Messages sortMessageThreads'); }
    const { activeUser: user } = environmentContext;
    
    if (user.groupTypeString === 'Facility') {
      sortMessageThreadsForFacilityUser();
    } else if (user.groupTypeString === 'Family') {
      sortMessageThreadsForFamilyUser();
    }
  }

  // NOTE: Because only Family users can create new threads
  // this method only needs to handle Family users
  const addNewThread = (newMessage) => {
    if (logComponent('Messages')) { console.debug('Messages addNewThread'); }
    setThreads([newMessage.userMessageThread, ...threads]);
    setVisibleThread(newMessage.userMessageThread);
    setCurrentMobileView('thread');
  }

    
  return (
    <div className="Messages">
      <div className="Messages__main">

        {
          !singlePanelView ?
            <React.Fragment>
              <p className="Messages__heading">Messages</p>
              <div className="Messages__section-header-bar">
                <div className="Messages__section-header-bar-inner">
                  {
                    user.groupTypeString === 'Facility' &&
                    <p>Individuals</p>
                  }
                  <p>Conversations</p>
                  {
                    user.groupTypeString === 'Family' &&
                    <button
                      className="Messages__new-conversation"
                      onClick={showNewConversationModal}>
                      <FontAwesomeIcon className="Messages__new-conversation-icon" icon="comments" />
                      Begin new conversation
                    </button>
                  }
                </div>
              </div>
            </React.Fragment> :
            <div className="Messages__mobile-heading">{getMobileHeading()}</div>
        }
        {
          threads &&
          (!singlePanelView ?
            <React.Fragment>
              <div className="Messages__content">
                {
                  monitoredIndividualThreadCollections &&
                  <MonitoredIndividualThreadList
                    monitoredIndividualThreadCollections={monitoredIndividualThreadCollections}
                    selectedMonitoredIndividual={visibleMonitoredIndividual}
                    onSelect={setThreadsThis} />
                }
                {
                  threads &&
                  <ThreadList
                    threads={threads}
                    selectedThread={visibleThread}
                    onSelect={setVisibleThreadThis} />
                }
                <Thread
                  thread={visibleThread}
                  unreadThread={unreadVisibleThread}
                  updateThreadLastActive={updateVisibleThreadLastActive}
                  lastReload={lastReload} />
              </div>
            </React.Fragment> :
            <React.Fragment>
              {
                user.groupTypeString === 'Facility' && currentMobileView !== 'monitoredIndividuals' &&
                <div className="Messages__monitoredindividual-info-bar">
                  <div className="Messages__monitoredindividual-image">
                    {/* TODO: Replace with Monitored Individual image, if/when one becomes available */}
                    <DefaultMonitoredIndividual />
                  </div>
                  <div className="Messages__monitoredindividual-text">
                    <p className="Messages__monitoredindividual-name">{`${visibleMonitoredIndividual.legalFirstName} ${visibleMonitoredIndividual.lastName}`}</p>
                    <p className="Messages__monitoredindividual-units">
                      {
                        visibleMonitoredIndividual.monitoredIndividualUnits &&
                        visibleMonitoredIndividual.monitoredIndividualUnits.map((individualUnit, index) =>
                          <span key={index}>{(index > 0 ? ', ' : '') + individualUnit.unit.name}</span>)
                      }
                    </p>
                  </div>
                </div>
              }

              <div className="Messages__content">
                {
                  currentMobileView === 'monitoredIndividuals' && monitoredIndividualThreadCollections &&
                  <MonitoredIndividualThreadList
                    fullWidth={true}
                    monitoredIndividualThreadCollections={monitoredIndividualThreadCollections}
                    selectedMonitoredIndividual={visibleMonitoredIndividual}
                    onSelect={setThreadsThis} />
                }
                {
                  currentMobileView === 'threads' && threads &&
                  <ThreadList
                    fullWidth={true}
                    threads={threads}
                    selectedThread={visibleThread}
                    onSelect={setVisibleThreadThis}
                    openConversationModal={showNewConversationModal} />
                }
                {
                  currentMobileView === 'threads' && threads &&
                  user.groupTypeString === 'Family' &&
                  <div className="Messages__conversation-button-wrapper">
                    <button
                      className="Messages__new-conversation--mobile"
                      onClick={showNewConversationModal}>
                      <FontAwesomeIcon className="Messages__new-conversation-icon--mobile" icon="comments" />
                      Begin new conversation
                    </button>
                  </div>
                }
                {
                  currentMobileView === 'thread' && visibleThread &&
                  <Thread
                    fullWidth={true}
                    thread={visibleThread}
                    unreadThread={unreadVisibleThread}
                    lastReload={lastReload}
                    updateThreadLastActive={updateVisibleThreadLastActive} />
                }
              </div>
            </React.Fragment>)
        }
        {
          threads && !threadsLoading && !threadsError &&
          threads.length === 0 &&
          <p className="Messages__empty-message">No messages found...</p>
        }
        <ModuleLoadingStatus isLoading={threadsLoading} isError={threadsError} reloadAction={loadMessageThreads} />
      </div>
      {
        newConversationModalIsVisible &&
        <NewConversationModal
          isVisible={newConversationModalIsVisible}
          hide={hideNewConversationModal}
          onSuccess={addNewThread} />
      }
    </div>
  );
}